// NotificationDropdown.tsx (Simplified - remove Tabs wrapper)
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  BellOff,
  BellRing,
  CheckCheck,
  Trash2,
  Volume2,
  VolumeX,
  MoreVertical,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { Separator } from "../ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { NotificationItem } from "./NotificationItem";
import { Notification } from "../../hooks/useNotifications";
import { usePushNotifications } from "../../hooks/usePushNotifications";
import { openNotification } from "./notificationTarget";

interface NotificationDropdownProps {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  hasMore: boolean;
  soundEnabled: boolean;
  toggleSound: () => void;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onDelete: (id: string) => void;
  onDeleteAllRead: () => void;
  onLoadMore: () => void;
  onClose: () => void;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  notifications,
  unreadCount,
  isLoading,
  hasMore,
  soundEnabled,
  toggleSound,
  onMarkAsRead,
  onMarkAllAsRead,
  onDelete,
  onDeleteAllRead,
  onLoadMore,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { status: pushStatus, busy: pushBusy, enable: enablePush, disable: disablePush } =
    usePushNotifications();

  const togglePush = async () => {
    if (pushStatus === "on") {
      await disablePush();
      toast.success(t("push_disabled"));
    } else {
      const ok = await enablePush();
      if (ok) toast.success(t("push_enabled"));
      else toast.error(t("push_enable_failed"));
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read if not already read
    if (!notification.isRead) {
      onMarkAsRead(notification._id);
    }

    // A click always lands somewhere readable: the notification's own
    // destination when it has one, otherwise the full notifications view.
    onClose();
    if (!openNotification(notification, navigate)) {
      navigate("/notifications");
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header Actions */}
      <div className="flex items-center justify-end flex-shrink-0 gap-1 p-2 border-b bg-muted/30">
        {/* Push on this device */}
        {pushStatus !== "unsupported" && (
          <Button
            variant="ghost"
            size="icon"
            className="w-8 h-8"
            onClick={togglePush}
            disabled={pushBusy || pushStatus === "denied"}
            title={
              pushStatus === "on"
                ? t("push_disable_tooltip")
                : pushStatus === "denied"
                ? t("push_denied_tooltip")
                : t("push_enable_tooltip")
            }
          >
            {pushStatus === "on" ? (
              <BellRing className="w-4 h-4 text-primary" />
            ) : (
              <BellOff className="w-4 h-4" />
            )}
          </Button>
        )}
        {/* Sound toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="w-8 h-8"
          onClick={toggleSound}
          title={soundEnabled ? t("disable_sound") : t("enable_sound")}
        >
          {soundEnabled ? (
            <Volume2 className="w-4 h-4" />
          ) : (
            <VolumeX className="w-4 h-4" />
          )}
        </Button>

        {/* Actions menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="w-8 h-8">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={onMarkAllAsRead}
              disabled={unreadCount === 0}
            >
              <CheckCheck className="w-4 h-4 mr-2" />
              {t("mark_all_as_read")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={onDeleteAllRead}
              disabled={notifications.every((n) => !n.isRead)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t("delete_all_read")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Notifications list */}
      <div className="flex-1 overflow-hidden">
        {isLoading && notifications.length === 0 ? (
          <div className="flex items-center justify-center h-full p-8">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-muted/50">
              <CheckCheck className="w-8 h-8 opacity-50 text-muted-foreground" />
            </div>
            <h4 className="mb-1 text-sm font-medium">
              {t("no_notifications_yet")}
            </h4>
            <p className="text-xs text-muted-foreground">
              You're all caught up!
            </p>
          </div>
        ) : (
          <ScrollArea className="h-full">
            <div className="divide-y">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification._id}
                  notification={notification}
                  onClick={() => handleNotificationClick(notification)}
                  onMarkAsRead={() => onMarkAsRead(notification._id)}
                  onDelete={() => onDelete(notification._id)}
                />
              ))}

              {/* Load more button */}
              {hasMore && (
                <>
                  <Separator />
                  <div className="p-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onLoadMore}
                      disabled={isLoading}
                      className="w-full hover:bg-accent"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t("loading")}
                        </>
                      ) : (
                        t("load_more")
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </ScrollArea>
        )}
      </div>

      {/* Footer — the way into the extended view */}
      <div className="flex-shrink-0 border-t p-2">
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-primary hover:text-primary"
          onClick={() => {
            onClose();
            navigate("/notifications");
          }}
        >
          {t("notif_view_all")}
        </Button>
      </div>
    </div>
  );
};